// JdForm.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Stack } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

function JdForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jdData } = location.state || {};

  // Try read generated data from localStorage (if present)
  const getGeneratedJdData = () => {
    try {
      const storedData = localStorage.getItem('generatedJdData');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        localStorage.removeItem('generatedJdData');
        return parsed;
      }
    } catch (err) {
      console.error('Error reading generatedJdData', err);
    }
    return null;
  };

  // Normalize incoming salaryRange to a friendly structure or string
  const normalizeSalary = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      // common shape: { min: 50000, max: 80000, currency: 'USD' }
      const { min, max, currency } = val;
      if (min || max) {
        return `${currency ? currency + ' ' : ''}${min ?? ''}${min && max ? ' - ' : ''}${max ?? ''}`;
      }
      // fallback to JSON
      return JSON.stringify(val);
    }
    return String(val);
  };

  // Normalize backend values to match frontend options
  const normalizeEnum = (val, map) => {
    if (!val) return '';
    const key = val.toLowerCase().replace(/[-\s]/g, ''); // e.g., "Full-time" -> "fulltime"
    return map.includes(key) ? key : '';
  };

  const initialFormData = {
    jobTitle: '',
    jobType: '',          // remote|hybrid|office
    companyLocation: '',
    experienceRange: '',
    employmentType: '',
    salaryRange: '',      // we keep a readable string here
    preferredQualification: '',
    jobSummary: '',
    keyResponsibilities: '',
    requiredSkills: '',
    optionalSkills: '',
    softSkills: '',
    workHours: '',
    companyCulture: '',
    travelRequirement: '',
    preferenceOne: '',
    preferenceTwo: '',
    preferenceThree: '',
    hiringProcess: '',
    applicationDeadline: '',
    mandatoryProfile: false,
    collegesApplicable: 'Any college', // New field with default value
    // optional penalty fields (if generated by AI)
    criticalSkills: [],
    skillPenalties: {}
  };

  const generatedData = getGeneratedJdData();
  const seed = generatedData || jdData || initialFormData;

  const EMPLOYMENT_OPTIONS = ['fulltime', 'parttime', 'contract', 'internship'];
  const JOBTYPE_OPTIONS = ['remote', 'hybrid', 'office'];

  // If server sent salary as object, normalize to string so it's visible in input
  if (seed) {
    seed.employmentType = normalizeEnum(seed.employmentType, EMPLOYMENT_OPTIONS);
    
    // Normalize jobType — handle "On-site" or "In Office" gracefully
    let normalizedJobType = normalizeEnum(seed.jobType, JOBTYPE_OPTIONS);
    if (!normalizedJobType && typeof seed.jobType === 'string') {
      const jt = seed.jobType.toLowerCase();
      if (jt.includes('on-site') || jt.includes('onsite') || jt.includes('in office')) {
        normalizedJobType = 'office';
      }
    }
    seed.jobType = normalizedJobType;
  
    // Normalize salary range if needed
    if (seed.salaryRange) {
      seed.salaryRange = normalizeSalary(seed.salaryRange);
    }

    // Ensure criticalSkills is an array and skillPenalties is an object
    if (seed.criticalSkills && typeof seed.criticalSkills === 'string') {
      try {
        seed.criticalSkills = JSON.parse(seed.criticalSkills);
      } catch {
        seed.criticalSkills = seed.criticalSkills.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    if (seed.skillPenalties && typeof seed.skillPenalties === 'string') {
      try {
        seed.skillPenalties = JSON.parse(seed.skillPenalties);
      } catch {
        seed.skillPenalties = {};
      }
    }

    // Ensure collegesApplicable has a default value if not present
    if (!seed.collegesApplicable) {
      seed.collegesApplicable = 'Any college';
    }
  }

  const [formData, setFormData] = useState(seed);
  const [newCriticalSkill, setNewCriticalSkill] = useState('');
  const [newPenalty, setNewPenalty] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Critical Skills Management
  const addCriticalSkill = () => {
    if (newCriticalSkill.trim() && newPenalty) {
      const penalty = parseInt(newPenalty);
      if (penalty >= 10 && penalty <= 25) {
        setFormData(prev => ({
          ...prev,
          criticalSkills: [...prev.criticalSkills, newCriticalSkill.trim()],
          skillPenalties: {
            ...prev.skillPenalties,
            [newCriticalSkill.trim()]: penalty
          }
        }));
        setNewCriticalSkill('');
        setNewPenalty('');
      } else {
        alert('Penalty must be between 10-25%');
      }
    }
  };

  const removeCriticalSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      criticalSkills: prev.criticalSkills.filter(s => s !== skill),
      skillPenalties: Object.fromEntries(
        Object.entries(prev.skillPenalties).filter(([s]) => s !== skill)
      )
    }));
  };

  // Helper to present salary value in UI
  const getSalaryDisplay = (salaryVal) => {
    if (!salaryVal) return '';
    return salaryVal;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare payload: if salaryRange was entered as "USD 50k - 70k" keep as string,
      // if you want structured salary on backend, you can parse here before sending.
      // const payload = { ...formData };
      navigate('/afterjd', { state: { jdData: formData } });

      // If criticalSkills or skillPenalties exist in state (from generated JD) we pass them along
      // send POST to your create_jd endpoint
      // const res = await fetch('/api/create_jd', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(payload)
      // });

      // const result = await res.json();
      // if (res.ok) {
      //   console.log('JD saved/created', result);
      //   // navigate to after page with jdData
      //   navigate('/afterjd', { state: { jdData: formData, job_id: result.job_id } });
      // } else {
      //   console.error('Error creating JD', result);
      //   alert(result.error || 'Failed to create JD');
      // }
    } catch (err) {
      console.error('submit error', err);
      alert('An error occurred while saving the JD. See console for details.');
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto border-border shadow-lg">
      <CardContent className="p-6 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Stack spacing="lg">
            <div>
              <Heading level="h3" className="text-xl font-semibold mb-4 pb-2 border-b-2 border-primary">Job Details</Heading>
              <Stack spacing="md" className="mt-4">
                <div>
                  <Label htmlFor="jobTitle">Job Title:</Label>
                  <Input
                    id="jobTitle"
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle || ''}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="companyLocation">Location:</Label>
                  <Input
                    id="companyLocation"
                    type="text"
                    name="companyLocation"
                    value={formData.companyLocation || ''}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Employment Type:</Label>
                  <RadioGroup
                    value={formData.employmentType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, employmentType: value }))}
                    className="flex flex-wrap gap-4 mt-2"
                  >
                    {['fulltime','parttime','contract','internship'].map(opt => (
                      <div key={opt} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt} id={`employment-${opt}`} />
                        <Label htmlFor={`employment-${opt}`} className="font-normal cursor-pointer">
                          {opt === 'fulltime' ? 'Full-time' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label htmlFor="experienceRange">Experience Range:</Label>
                  <Input
                    id="experienceRange"
                    type="text"
                    name="experienceRange"
                    value={formData.experienceRange || ''}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Job Type:</Label>
                  <RadioGroup
                    value={formData.jobType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, jobType: value }))}
                    className="flex flex-wrap gap-4 mt-2"
                  >
                    {['remote','hybrid','office'].map(opt => (
                      <div key={opt} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt} id={`jobtype-${opt}`} />
                        <Label htmlFor={`jobtype-${opt}`} className="font-normal cursor-pointer">
                          {opt === 'office' ? 'In Office' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label htmlFor="salaryRange">Salary Range:</Label>
                  <Input
                    id="salaryRange"
                    type="text"
                    name="salaryRange"
                    value={getSalaryDisplay(formData.salaryRange)}
                    onChange={handleChange}
                    placeholder="e.g. USD 50k - 70k or 50000-70000"
                    className="mt-1"
                  />
                </div>
              </Stack>
            </div>

            <Separator />

            <div>
              <Heading level="h3" className="text-xl font-semibold mb-4 pb-2 border-b-2 border-primary">Qualifications & Summary</Heading>
              <Stack spacing="md" className="mt-4">
                <div>
                  <Label htmlFor="preferredQualification">Preferred Qualification (Optional)</Label>
                  <Select
                    name="preferredQualification"
                    value={formData.preferredQualification || ''}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, preferredQualification: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select Qualification" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High School">High School</SelectItem>
                      <SelectItem value="Bachelor's Degree">Bachelor's Degree</SelectItem>
                      <SelectItem value="Master's Degree">Master's Degree</SelectItem>
                      <SelectItem value="PhD">PhD</SelectItem>
                      <SelectItem value="Diploma">Diploma</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="collegesApplicable">Colleges Applicable:</Label>
                  <Input
                    id="collegesApplicable"
                    type="text"
                    name="collegesApplicable"
                    value={formData.collegesApplicable || 'Any college'}
                    onChange={handleChange}
                    placeholder="e.g., Any college, IITs only, Top 100 colleges, etc."
                    className="mt-1"
                  />
                  <Text variant="muted" className="text-xs mt-1">
                    Specify which colleges this job is applicable for. Default is "Any college".
                  </Text>
                </div>

                <div>
                  <Label htmlFor="jobSummary">Job Summary</Label>
                  <Textarea
                    id="jobSummary"
                    name="jobSummary"
                    rows={4}
                    value={formData.jobSummary || ''}
                    onChange={handleChange}
                    placeholder="Enter job summary here..."
                    className="mt-1"
                  />
                </div>
              </Stack>
            </div>

            <Separator />

            <div>
              <Heading level="h3" className="text-xl font-semibold mb-4 pb-2 border-b-2 border-primary">Responsibilities & Skills</Heading>
              <Stack spacing="md" className="mt-4">
                <div>
                  <Label htmlFor="keyResponsibilities">Key Responsibilities</Label>
                  <Textarea
                    id="keyResponsibilities"
                    name="keyResponsibilities"
                    rows={4}
                    value={formData.keyResponsibilities || ''}
                    onChange={handleChange}
                    placeholder="Enter key responsibilities here..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="requiredSkills">Required Skills (comma separated):</Label>
                  <Input
                    id="requiredSkills"
                    type="text"
                    name="requiredSkills"
                    value={formData.requiredSkills || ''}
                    onChange={handleChange}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="optionalSkills">Good to have skills (Optional):</Label>
                  <Input
                    id="optionalSkills"
                    type="text"
                    name="optionalSkills"
                    value={formData.optionalSkills || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
              </Stack>
            </div>

            <Separator />

            <div>
              <Heading level="h3" className="text-xl font-semibold mb-4 pb-2 border-b-2 border-primary">Work & Culture Details (Optional)</Heading>
              <Stack spacing="md" className="mt-4">
                <div>
                  <Label htmlFor="softSkills">Soft Skills:</Label>
                  <Textarea
                    id="softSkills"
                    name="softSkills"
                    rows={3}
                    value={formData.softSkills || ''}
                    onChange={handleChange}
                    placeholder="E.g., Creativity, Communication"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="workHours">Work Hours & Flexibility:</Label>
                  <Select
                    name="workHours"
                    value={formData.workHours || ''}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, workHours: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fixed">Fixed</SelectItem>
                      <SelectItem value="Flexible">Flexible</SelectItem>
                      <SelectItem value="Remote">Remote</SelectItem>
                      <SelectItem value="Hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="companyCulture">Company Culture & Perks (Optional):</Label>
                  <Textarea
                    id="companyCulture"
                    name="companyCulture"
                    rows={3}
                    value={formData.companyCulture || ''}
                    onChange={handleChange}
                    placeholder="E.g., Growth, Mentorship"
                    className="mt-1"
                  />
                </div>
              </Stack>
            </div>

            <Separator />

            <div>
              <Heading level="h3" className="text-xl font-semibold mb-4 pb-2 border-b-2 border-primary">Critical Skills & Penalties</Heading>
              <Text variant="muted" className="text-sm mb-4">
                Define absolutely essential skills and penalty percentages for missing skills (10-25% penalty per skill).
                Candidates missing these skills will receive significant score reductions.
              </Text>
              
              <div className="flex gap-2 mb-4">
                <Input
                  type="text"
                  placeholder="Critical skill (e.g., Python, React, SQL)"
                  value={newCriticalSkill}
                  onChange={(e) => setNewCriticalSkill(e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="number"
                  placeholder="Penalty %"
                  value={newPenalty}
                  onChange={(e) => setNewPenalty(e.target.value)}
                  min="10"
                  max="25"
                  className="w-32"
                />
                <Button type="button" onClick={addCriticalSkill} variant="outline">
                  Add Skill
                </Button>
              </div>

              {formData.criticalSkills.length > 0 && (
                <div className="space-y-2">
                  <Heading level="h4" className="text-base font-semibold">Critical Skills & Penalties:</Heading>
                  {formData.criticalSkills.map(skill => (
                    <div key={skill} className="flex items-center justify-between p-3 bg-muted rounded-md">
                      <div className="flex items-center gap-3">
                        <Text className="font-medium">{skill}</Text>
                        <Text variant="muted" className="text-sm">{formData.skillPenalties[skill]}% penalty</Text>
                      </div>
                      <Button
                        type="button"
                        onClick={() => removeCriticalSkill(skill)}
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            <div>
              <Heading level="h3" className="text-xl font-semibold mb-4 pb-2 border-b-2 border-primary">Preferences</Heading>
              <Stack spacing="md" className="mt-4">
                <Input
                  type="text"
                  placeholder="Preference One"
                  name="preferenceOne"
                  value={formData.preferenceOne || ''}
                  onChange={handleChange}
                />
                <Input
                  type="text"
                  placeholder="Preference Two"
                  name="preferenceTwo"
                  value={formData.preferenceTwo || ''}
                  onChange={handleChange}
                />
                <Input
                  type="text"
                  placeholder="Preference Three"
                  name="preferenceThree"
                  value={formData.preferenceThree || ''}
                  onChange={handleChange}
                />
              </Stack>
            </div>

            <Separator />

            <div>
              <Heading level="h3" className="text-xl font-semibold mb-4 pb-2 border-b-2 border-primary">Additional Requirements & Hiring Process</Heading>
              <Stack spacing="md" className="mt-4">
                <div>
                  <Label htmlFor="travelRequirement">Travel Requirement (Optional):</Label>
                  <Select
                    name="travelRequirement"
                    value={formData.travelRequirement || ''}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, travelRequirement: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Occasionally">Occasionally</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="hiringProcess">Hiring Process & Timeline (Optional):</Label>
                  <Textarea
                    id="hiringProcess"
                    name="hiringProcess"
                    rows={4}
                    value={formData.hiringProcess || ''}
                    onChange={handleChange}
                    placeholder="E.g., Resume -> Tech test -> Interview"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="applicationDeadline">Application Deadline:</Label>
                  <Input
                    id="applicationDeadline"
                    type="date"
                    name="applicationDeadline"
                    value={formData.applicationDeadline || ''}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Audio/Video Profile:</Label>
                  <Text variant="muted" className="text-sm mt-1 mb-3">
                    Reduce time spent on screening candidates! Review a candidate's Video/Audio Profile easily.
                  </Text>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="mandatoryProfile"
                      checked={!!formData.mandatoryProfile}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, mandatoryProfile: checked }))}
                    />
                    <Label htmlFor="mandatoryProfile" className="font-normal cursor-pointer">
                      Mark as mandatory
                    </Label>
                  </div>
                </div>
              </Stack>
            </div>

            <Button type="submit" className="w-full mt-6" size="lg">
              Apply Changes
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
}

export default JdForm;